import { Disc3, Music2, PlayCircle } from "lucide-react";
import { Box, Link, Tab, Tabs, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { usePlaylist } from "../../../contexts/PlaylistContext";
import useAppleMusic from "../../../hooks/useAppleMusic";
import useGetNewestTrack from "../../../hooks/useGetNewestTrack";
import useItunes from "../../../hooks/useItunes";
import useLoadTracksWithPreviews from "../../../hooks/useLoadTracksWithPreviews";
import useWindowSize from "../../../hooks/useWindowSize";
import { selectItunesEnrichment } from "../../../utils/appleMusicEnrichment";
import { availableMusicTabs, MUSIC_RELEASE_TABS, releasesForTab, resolveMusicTab } from "../../../utils/musicReleaseTabs";
import { BREAKPOINT, formatDate, getArtistPath, lc, safeStringWithHyphens, trackClick } from "../../_helpers";
import ReleaseBento, { type BentoRelease } from "../../cards/ReleaseBento";
import FeaturedReleaseCard from "../../cards/FeaturedReleaseCard";
import { VINYL_HOST_CLASS } from "../../../hooks/useVinylReveal";
import MusicPlayer from "./MusicPlayer";
import { PopularTracks } from "./PopularTracks";

export function MusicSection(props) {
  const [tabValue, setTabValue] = useState("albums");
  const { song, artistData } = props;
  const { enqueueTracks, playlist, setAlbums, setArtistName, isPlayerVisible, currentTrack, isPlaying } = usePlaylist();
  const { loadTracks, isLoading, loadedCount } = useLoadTracksWithPreviews();

  const windowSize = useWindowSize();
  const reallyIsMobile = windowSize.width <= BREAKPOINT;
  const newestRelease = useGetNewestTrack(
    {
      artistName: artistData.name,
    },
    undefined,
    [artistData.name]
  );

  const newestReleaseAlbum = newestRelease?.trackData?.album;
  const newestReleaseTrackCount = ((newestReleaseAlbum as any)?.total_tracks || 0);
  const newestReleaseLookupType = newestReleaseTrackCount >= 3 ? "album" : "song";
  const newestReleaseLookupName = newestReleaseLookupType === "album" ? newestReleaseAlbum?.name : newestRelease?.trackData?.name;
  const { url: newestReleaseAppleMusicUrl, previewUrl: newestReleaseApplePreviewUrl } = useAppleMusic(
    artistData.name,
    newestReleaseLookupName,
    newestReleaseLookupType,
    [artistData.name, newestReleaseLookupName, newestReleaseLookupType]
  );
  const shouldFetchItunes = newestRelease && !newestRelease?.trackData?.preview_url && !newestReleaseApplePreviewUrl;
  const { data: itunesData } = useItunes(
    shouldFetchItunes ? artistData.name : undefined,
    shouldFetchItunes ? newestRelease?.trackData?.name : undefined,
    5,
    [artistData.name, newestRelease?.trackData?.name]
  );
  const itunesMatch = selectItunesEnrichment(itunesData || undefined, {
    artistName: artistData.name,
    releaseName: newestRelease?.trackData?.name,
    lookupType: "song",
  });

  // Use Spotify preview if available, otherwise use iTunes preview
  const previewUrl = newestRelease?.trackData?.preview_url || newestReleaseApplePreviewUrl || itunesMatch?.previewUrl;

  console.log("newestRelease:", newestRelease, artistData);
  console.log("iTunes fallback data:", itunesData);

  // Auto-load first 10 tracks when component mounts and provide albums/artist to playlist context
  useEffect(() => {
    if (!song?.trackData?.albums || !artistData?.name) return;

    // Store albums and artist name in playlist context for later use
    if (setAlbums && setArtistName) {
      setAlbums(song.trackData.albums);
      setArtistName(artistData.name);
    }

    // Only load if we haven't loaded any tracks yet
    if (loadedCount === 0 && playlist.length === 0 && !isLoading) {
      console.log("🎵 Initial load: Loading first 10 tracks");
      loadTracks({
        albums: song.trackData.albums,
        artistName: artistData.name,
        limit: 10,
        onTrackLoaded: (trackData) => {
          enqueueTracks([trackData]);
        },
      });
    }
  }, [song?.trackData?.albums, artistData?.name]);

  const linkStyles = {
    color: "var(--logoColor2)",
    fontSize: { xs: "1.8rem", md: "0.75rem" },
    fontFamily: "Poppins",
    fontStyle: "italic",
    fontWeight: 700,
    lineHeight: 1,
    wordWrap: "break-word",
  };

  const tabStyle = {
    color: "var(--logoColor)",
    fontSize: { xs: "3rem", sm: "1.5rem" },
    fontFamily: "Hanson",
    fontWeight: 700,
  };

  const handleTabChange = (event, newValue) => {
    trackClick(`music-tab:${newValue}`, { label: `Music tab ${newValue}`, section: "music-section", artist: artistData?.name });
    setTabValue(newValue);
  };

  if (!song || !artistData) {
    return null;
  }

  const availableTabs = availableMusicTabs(song?.trackData?.albums);
  const selectedTab = resolveMusicTab(tabValue, availableTabs);

  const tabReleases = [...releasesForTab<BentoRelease>((song?.trackData?.albums || []) as BentoRelease[], selectedTab ?? "albums")].sort((a, b) =>
    String(b.release_date || "").localeCompare(String(a.release_date || "")),
  );
  const selectedTabLabel = selectedTab === "singles" ? "singles" : selectedTab === "eps" ? "EPs" : "albums";
  const releasePathFor = (release: { name: string; total_tracks?: number }) =>
    getArtistPath(song.artistUsername || lc(song.artist), ((release.total_tracks || 0) >= 3 ? "/albums/" : "/songs/") + safeStringWithHyphens(release.name));

  const hasNewestRelease = newestRelease && newestRelease.trackData;
  const albumData = hasNewestRelease ? newestRelease.trackData.album : null;
  const newestReleaseTotalTracks = ((albumData as any)?.total_tracks || 0);
  const newestReleaseType =
    newestReleaseTotalTracks >= 7
      ? "Album"
      : newestReleaseTotalTracks >= 3
        ? "EP"
        : "Single";
  const featuredPlaying = Boolean(isPlaying && albumData?.name && currentTrack?.albumName === albumData.name);
  const newestReleasePath = getArtistPath(
    artistData.name,
    (newestReleaseTotalTracks >= 3 ? "/albums/" : "/songs/") + safeStringWithHyphens(albumData?.name)
  );

  const musicActions = [
    { label: "Music", path: getArtistPath(artistData.name, "/songs"), icon: <Music2 /> },
    { label: "Albums", path: getArtistPath(artistData.name, "/albums"), icon: <Disc3 /> },
    { label: "Videos", path: getArtistPath(artistData.name, "/videos"), icon: <PlayCircle /> },
  ];

  const musicActionRow = (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        gap: { xs: "0.7rem", md: "0.9rem" },
        mt: { xs: 0, md: "4rem" },
      }}
    >
      {musicActions.map((action) => (
        <Box
          key={action.path}
          component={RouterLink}
          to={action.path}
          onClick={() => trackClick(`music-action:${action.path.split("/").pop()}`, { label: action.label, section: "music-section", artist: artistData?.name, href: action.path })}
          viewTransition
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: "0.35rem", md: "0.45rem" },
            minHeight: { xs: "64px", md: "3.35rem" },
            flex: 1,
            minWidth: 0,
            p: { xs: "0.85rem 0.65rem", md: "0.7rem 0.85rem" },
            borderRadius: "8px",
            backgroundColor: "color-mix(in srgb, var(--logoColor) 72%, var(--color5))",
            color: "var(--color5)",
            opacity: 0.78,
            border: "1px solid color-mix(in srgb, var(--logoColor) 42%, transparent)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.16), 0 0.65rem 1.25rem rgba(1,1,1,0.12)",
            textDecoration: "none",
            transition: "opacity 0.2s ease, transform 0.2s ease",
            "&:hover": {
              opacity: 0.72,
              transform: "translateY(-2px)",
            },
          }}
        >
          <Box
            sx={{
              color: "var(--color5)",
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              "& svg": {
                height: { xs: "20px", md: "1.15rem" },
                minWidth: { xs: "20px", md: "auto" },
                width: { xs: "20px", md: "1.15rem" },
                strokeWidth: 2.1,
              },
            }}
          >
            {action.icon}
          </Box>
          <Typography
            sx={{
              color: "var(--color5)",
              fontFamily: "Poppins",
              fontWeight: 700,
              fontSize: { xs: "15px", md: "0.9rem" },
              lineHeight: 1.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {action.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      className="music-section"
      sx={{
        // minHeight: { xs: "105rem", md: "50rem" },
        width: { xs: "calc(100vw - 5rem)", md: `calc(${windowSize.width}px - (2.5rem * 2))` },
        maxWidth: { xs: "calc(100vw - 5rem)", md: "none" },
        p: { xs: "2.5rem", md: "3.5rem" },
        m: { xs: "1rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        backgroundColor: "var(--color5)",
        borderRadius: "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: "2.5rem", sm: "10rem", md: 5 },
        zIndex: 1,
        transition: "width 0.45s ease, max-width 0.45s ease, margin 0.45s ease",
        "@keyframes musicSectionFillMobile": {
          from: { width: "calc(100vw - 7rem)", maxWidth: "calc(100vw - 7rem)" },
          to: { width: "calc(100vw - 5rem)", maxWidth: "calc(100vw - 5rem)" },
        },
        "@supports (animation-timeline: view())": {
          animation: { xs: "musicSectionFillMobile linear both", md: "none" },
          animationTimeline: "view()",
          animationRange: "entry 0% cover 32%",
        },
      }}
    >
      {!reallyIsMobile && isPlayerVisible && (
        <MusicPlayer
          audioUrl={previewUrl}
          sx={null}
          artist={artistData.name}
          song={newestRelease?.trackData?.name}
          artwork={newestRelease?.trackData?.album?.images?.[0]?.url || itunesMatch?.artworkUrl}
        />
      )}

      <Box
        component="h2"
        sx={{
          color: "var(--logoColor2)",
          fontSize: { xs: "3rem", md: "3.34rem" },
          fontFamily: "Hanson",
          fontWeight: 700,
          alignSelf: { xs: "flex-start", md: "auto" },
          margin: { xs: "0 0 0.5rem", md: "1.75rem 0 0.5rem" },
        }}
      >
        MUSIC
      </Box>

      <Box
        className="music-left-right"
        sx={{
          width: "100%",
          minHeight: { xs: "auto", md: "30.625rem" },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: "5rem",
          position: "relative",
        }}
      >
        {hasNewestRelease && albumData && (
          <Box
            className="music-left"
            sx={{
              width: { xs: "100%", md: "35%" },
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", md: "flex-start" },
              gap: { xs: "1.5rem", md: "2.1875rem" },
              paddingTop: "0.8125rem",
            }}
          >
            <Link
              className={`featured-song ${VINYL_HOST_CLASS}`}
              data-apple-music-url={newestReleaseAppleMusicUrl || undefined}
              sx={{
                display: "block",
                width: "100%",
                color: "inherit",
                textDecoration: "none",
                borderRadius: "24px",
                "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "4px" },
              }}
              to={newestReleasePath}
              component={RouterLink}
              viewTransition
              onClick={() => trackClick("featured-release", { label: albumData?.name || "Featured release", section: "music-section", artist: artistData?.name, releaseName: albumData?.name, href: newestReleasePath })}
            >
              <FeaturedReleaseCard
                title={albumData?.name || newestRelease.trackData.name}
                kindLabel={newestReleaseType}
                meta={[
                  (albumData as any)?.release_date ? formatDate((albumData as any).release_date) : "",
                  newestReleaseTotalTracks > 1 ? `${newestReleaseTotalTracks} tracks` : "",
                ]}
                imageUrl={albumData?.images?.[0]?.url}
                fallbackImageUrl={itunesMatch?.artworkUrl}
                playing={featuredPlaying}
                sharedKey={(albumData as any)?.id || albumData?.name}
              >
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    flexWrap: "wrap",
                    gap: "0.875rem",
                  }}
                  className="listen-on"
                >
                  <Typography
                    className="ListenOn"
                    variant="body2"
                    sx={{ ...linkStyles, fontWeight: 400, fontStyle: "normal" }}
                  >
                    Listen on
                  </Typography>
                  <Typography className="AppleMusic" variant="body2" sx={linkStyles}>
                    Apple Music
                  </Typography>
                  <Typography className="Spotify" variant="body2" sx={linkStyles}>
                    Spotify
                  </Typography>
                  <Typography className="Youtube" variant="body2" sx={linkStyles}>
                    YouTube
                  </Typography>
                </Box>
              </FeaturedReleaseCard>
            </Link>

            {reallyIsMobile && (
              <>
                <PopularTracks
                  artistName={artistData.name}
                  topTracks={artistData.topTracks}
                  albums={song.trackData.albums}
                  mobileLatestOnly
                  hideLatestHeader
                  featuredReleaseId={(albumData as any)?.id}
                  featuredReleaseName={(albumData as any)?.name}
                />
                {musicActionRow}
              </>
            )}
          </Box>
        )}

        <Box
          className="music-releases"
          sx={{
            display: { xs: "none", md: "block" },
            width: { xs: "100%", md: "65%" },
            maxWidth: { xs: "100%", md: "54vw" },
            height: "100%",
            p: "0",
            position: "relative",
            ".MuiButtonBase-root": {
              textTransform: "capitalize",
              padding: { xs: "3rem 3rem 5rem", md: ".75rem 1.75rem 2.625rem" },
              borderTopLeftRadius: "1rem",
              borderTopRightRadius: "1rem",
              "&.Mui-selected": {
                color: "var(--logoColor)",
                opacity: 1,
              },
            },
            ".MuiTabs-indicator": {
              backgroundColor: "var(--logoColor2)",
              height: "3px",
            },
          }}
        >
          {selectedTab && (
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              sx={{ mb: "2.3rem", borderBottom: "1px solid var(--color1)" }}
            >
              {MUSIC_RELEASE_TABS.filter((tab) => availableTabs.includes(tab.value)).map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} sx={tabStyle} />
              ))}
            </Tabs>
          )}
          <ReleaseBento
            releases={tabReleases}
            pathFor={releasePathFor}
            allHref={getArtistPath(artistData.name, "/albums")}
            allLabel={selectedTabLabel}
            isPlaying={(release) => Boolean(isPlaying && currentTrack?.albumName && currentTrack.albumName === release.name)}
            onOpen={(release, href) => trackClick("album-card", { label: release.name, section: "album-list", releaseName: release.name, href })}
            onOpenAll={() => trackClick("music-bento-all", { label: `All ${selectedTabLabel}`, section: "music-section", artist: artistData?.name, href: getArtistPath(artistData.name, "/albums") })}
            emptyLabel={`No ${selectedTabLabel} found`}
          />
        </Box>
      </Box>

      {!reallyIsMobile && (
        <Box sx={{ width: "100%" }}>
          <PopularTracks
            artistName={artistData.name}
            topTracks={artistData.topTracks}
            albums={song.trackData.albums}
            featuredReleaseId={(albumData as any)?.id}
            featuredReleaseName={(albumData as any)?.name}
          />
          {musicActionRow}
        </Box>
      )}
    </Box>
  );
}
